// Reglas derivadas de las políticas especiales, del mínimo del abono del plan y del aviso de fallo
// de la bitácora (puras, sin React). No conocen el wire: eso vive en `api/*Mappers.ts`.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { ApiError, userMessageOf } from '@/api/errors';
import {
    STORE_POLICY_ERROR_CODES,
    type StorePolicyErrorCode,
} from '@/api/services/loan-offerings/contract';
import { formatDayMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import type { TierValue } from '@/shared/patterns/Pills';
import { classifyFailure, describeFailure, isRetryableFailure } from '../creditFile/failures';
import { POLICIES_PERMISSIONS } from './permissions';
import {
    EASABLE_LEVERS,
    POLICY_LEVEL_LABEL,
    POLICY_LEVER_LABEL,
    type PlanPrepaymentMinimum,
    type PlanTier,
    type PolicyAction,
    type PolicyDraft,
    type PolicyLeverKey,
    type PolicyLevers,
    type PolicyStatus,
    type PolicyType,
    type SpecialPolicy,
    type StorePolicyStatus,
} from './types';

/**
 * El nivel del plan en las llaves con las que habla el scoring. `PlanTier` es un espacio paralelo
 * al de `ClientLevelEnum`, y el empleado no puntúa: la píldora lo pinta con su propia llave.
 */
const LEVEL_BY_PLAN_TIER: Record<PlanTier, TierValue> = {
    bronce: ClientLevelEnum.BRONZE,
    plata: ClientLevelEnum.SILVER,
    oro: ClientLevelEnum.GOLD,
    empleado: 'employee',
};

/**
 * Lo mismo que valida loan-offerings para el mínimo del abono: % en (0, 100] y monto de al menos
 * $0.01. `null` cuando el mínimo se puede publicar.
 */
export function prepaymentMinimumError(minimum: PlanPrepaymentMinimum): string | null {
    if (minimum.mode === 'PERCENT_OF_PERIOD_PAYMENT') {
        const pct = minimum.periodPaymentPct;
        if (pct === null || pct === undefined || !Number.isFinite(pct)) {
            return 'Captura el porcentaje del pago semanal.';
        }
        return pct > 0 && pct <= 1 ? null : 'El porcentaje debe ser de 0.01 % a 100 %.';
    }
    if (minimum.mode === 'FIXED') {
        const amount = minimum.amount;
        if (amount === null || amount === undefined || !Number.isFinite(amount)) {
            return 'Captura el monto mínimo.';
        }
        return Math.round(amount * 100) >= 1 ? null : 'El monto mínimo es de al menos $0.01.';
    }
    return null;
}

/** El mínimo del abono en una línea: «Cualquier monto», «50 % del pago semanal» o «$100.00». */
export function prepaymentMinimumLabel(minimum: PlanPrepaymentMinimum): string {
    if (
        minimum.mode === 'PERCENT_OF_PERIOD_PAYMENT' &&
        typeof minimum.periodPaymentPct === 'number'
    ) {
        return `${String(decimalToPoints(minimum.periodPaymentPct))} % del pago semanal`;
    }
    if (minimum.mode === 'FIXED' && typeof minimum.amount === 'number') {
        return formatMXN(minimum.amount);
    }
    return 'Cualquier monto';
}

/** El puente entre el nivel del plan y el que entiende la píldora de nivel. */
export function planTierToLevel(tier: PlanTier): TierValue {
    return LEVEL_BY_PLAN_TIER[tier];
}

const LEVER_TO_TYPE: Record<PolicyLeverKey, PolicyType> = {
    sciMin: 'sci_min',
    downMinPct: 'down_min',
    tna: 'tna',
    maxAmount: 'max_amount',
};

export const LEVER_KEYS: readonly PolicyLeverKey[] = ['sciMin', 'downMinPct', 'tna', 'maxAmount'];

/** Las palancas prendidas, en el orden fijo de la ficha. */
export function activeLevers(levers: PolicyLevers): PolicyLeverKey[] {
    return LEVER_KEYS.filter((key) => levers[key] !== undefined);
}

/**
 * Tipo primario deducido: suspender es exclusivo · 1 palanca = tipo puro · 2+ = combo. La compuerta
 * de SKU cuenta como una palanca más: sin eso, una política que solo restringe SKU se leía vacía.
 */
export function derivePolicyType(
    policy: Pick<SpecialPolicy, 'suspend' | 'levers' | 'allowedSkus'>,
): PolicyType {
    if (policy.suspend) return 'suspend';
    const active = activeLevers(policy.levers);
    const gates = policy.allowedSkus.length > 0 ? 1 : 0;
    if (active.length + gates >= 2) return 'combo';
    if (active.length === 1) return LEVER_TO_TYPE[active[0]];
    if (gates === 1) return 'allowed_skus';
    // Una política sin palancas ni compuertas no existe en el backend (400 al guardarla).
    return 'suspend';
}

/**
 * El estado que lee el operador. El `status` persistido manda; la `phase` que el backend deriva de
 * las fechas solo desempata dentro de PUBLISHED.
 *
 * El front NO recalcula la fase desde las fechas: dos derivaciones de lo mismo terminan con la
 * lista y el detalle diciendo cosas distintas. Sin fase, PUBLISHED se lee «Activa».
 */
export function policyView(policy: Pick<SpecialPolicy, 'status' | 'phase'>): PolicyStatus {
    if (policy.status === 'ARCHIVED') return 'ARCHIVED';
    if (policy.status === 'DRAFT') return 'DRAFT';
    if (policy.status === 'PAUSED') return 'PAUSED';
    if (policy.status === 'EXPIRED') return 'EXPIRED';
    if (policy.phase === 'SCHEDULED') return 'SCHEDULED';
    if (policy.phase === 'EXPIRED') return 'EXPIRED';
    return 'ACTIVE';
}

/** Qué se le ofrece al operador, leído de la MISMA vista que pinta el badge. */
export function allowedActions(view: PolicyStatus): PolicyAction[] {
    switch (view) {
        case 'DRAFT':
            return ['publish', 'archive'];
        case 'SCHEDULED':
        case 'ACTIVE':
            return ['pause', 'expire', 'archive'];
        case 'PAUSED':
            return ['resume', 'expire', 'archive'];
        case 'EXPIRED':
            // Vencida es justo la que el operador quiere sacar de la lista: eliminar es lo único
            // que le queda.
            return ['archive'];
        case 'ARCHIVED':
            return [];
    }
}

/** El `status` que viaja en el `PUT /store-policies/{id}/status` de cada acción. */
export function actionTargetStatus(action: PolicyAction): StorePolicyStatus {
    switch (action) {
        case 'publish':
            return 'PUBLISHED';
        case 'pause':
            return 'PAUSED';
        case 'resume':
            return 'PUBLISHED';
        case 'expire':
            return 'EXPIRED';
        case 'archive':
            return 'ARCHIVED';
    }
}

/** Editar solo tiene sentido antes de cerrar: EXPIRED y ARCHIVED son terminales en el backend. */
export function canEditPolicy(status: StorePolicyStatus): boolean {
    return status !== 'EXPIRED' && status !== 'ARCHIVED';
}

/** Pausar, vencer y eliminar aceptan motivo, y queda escrito en la bitácora. */
export function actionTakesReason(action: PolicyAction): boolean {
    return action === 'pause' || action === 'expire' || action === 'archive';
}

/**
 * `archive` es la única que EXIGE el motivo: el lambda responde 422 sin él, y es lo único que queda
 * en la bitácora para explicar por qué la política desapareció de la pantalla.
 */
export function actionRequiresReason(action: PolicyAction): boolean {
    return action === 'archive';
}

export interface PolicyActionCopy {
    label: string;
    title: string;
    description: string;
    confirmLabel: string;
    destructive: boolean;
}

const ACTION_COPY: Record<PolicyAction, PolicyActionCopy> = {
    publish: {
        label: 'Publicar',
        title: '¿Publicas esta política?',
        description:
            'Empieza a aplicarse a las ventas de su alcance dentro de la vigencia. Después de publicar solo puedes pausarla o vencerla.',
        confirmLabel: 'Publicar',
        destructive: false,
    },
    pause: {
        label: 'Pausar',
        title: '¿Pausas esta política?',
        description:
            'Deja de aplicarse mientras esté pausada. Puedes reanudarla y su vigencia no cambia.',
        confirmLabel: 'Pausar',
        destructive: false,
    },
    resume: {
        label: 'Reanudar',
        title: '¿Reanudas esta política?',
        description: 'Vuelve a aplicarse dentro de su vigencia, con el mismo efecto que tenía.',
        confirmLabel: 'Reanudar',
        destructive: false,
    },
    expire: {
        label: 'Vencer ahora',
        title: '¿Vences esta política?',
        description:
            'Es definitivo: una política vencida no se puede reanudar ni volver a editar. No hay forma de borrarla, vencerla es el cierre.',
        confirmLabel: 'Vencer',
        destructive: true,
    },
    archive: {
        label: 'Eliminar',
        title: '¿Eliminas esta política?',
        description:
            'Desaparece de esta pantalla y deja de aplicarse. No se puede recuperar. Las cotizaciones que ya la plegaron la conservan, y la bitácora guarda el evento.',
        confirmLabel: 'Eliminar',
        destructive: true,
    },
};

export function actionCopy(action: PolicyAction): PolicyActionCopy {
    return ACTION_COPY[action];
}

// ---------------------------------------------------------------------------------------------
// Vigencia y alcance en texto
// ---------------------------------------------------------------------------------------------

/** La vigencia en una línea, según el estado que lee el operador. */
export function validityLabel(policy: Pick<SpecialPolicy, 'status' | 'phase' | 'validTo'>): string {
    const view = policyView(policy);
    switch (view) {
        case 'DRAFT':
            return 'Sin publicar';
        case 'PAUSED':
            return 'Pausada';
        case 'EXPIRED':
            return policy.validTo ? `Venció ${formatDayMx(policy.validTo)}` : 'Vencida';
        case 'SCHEDULED':
            return 'Programada';
        case 'ACTIVE':
            return policy.validTo ? `Hasta ${formatDayMx(policy.validTo)}` : 'Permanente';
        case 'ARCHIVED':
            return 'Eliminada';
    }
}

/** El alcance de una política en partes, para pintarlas resaltadas. Vacío en todas = toda la red. */
export function describeScope(
    scope: Pick<PolicyDraft, 'segmentIds' | 'targetLevels' | 'storeIds' | 'productSkus'>,
): string[] {
    const segments =
        scope.segmentIds.length === 0
            ? 'todos los segmentos'
            : `${String(scope.segmentIds.length)} ${scope.segmentIds.length === 1 ? 'segmento' : 'segmentos'}`;
    const levels =
        scope.targetLevels.length === 0
            ? 'todos los niveles'
            : scope.targetLevels.map((level) => POLICY_LEVEL_LABEL[level]).join(' · ');
    const stores =
        scope.storeIds.length === 0
            ? 'todas las tiendas'
            : `${String(scope.storeIds.length)} ${scope.storeIds.length === 1 ? 'tienda' : 'tiendas'}`;
    const products =
        scope.productSkus.length === 0
            ? 'todos los productos'
            : `${String(scope.productSkus.length)} ${scope.productSkus.length === 1 ? 'producto' : 'productos'}`;
    // El orden espeja las columnas del alcance: Segmento, Nivel, Tienda, Producto.
    return [segments, levels, stores, products];
}

// ---------------------------------------------------------------------------------------------
// Borrador: crear, comparar y validar antes de mandarlo
// ---------------------------------------------------------------------------------------------

/** El borrador de una política existente — lo editable, sin estado ni versión. */
export function draftFromPolicy(policy: SpecialPolicy): PolicyDraft {
    return {
        name: policy.name,
        description: policy.description,
        suspend: policy.suspend,
        levers: { ...policy.levers },
        allowedSkus: [...policy.allowedSkus],
        targetLevels: [...policy.targetLevels],
        storeIds: [...policy.storeIds],
        productSkus: [...policy.productSkus],
        segmentIds: [...policy.segmentIds],
        validFrom: policy.validFrom,
        validTo: policy.validTo,
    };
}

/** Una política nueva: nace sin alcance (toda la red) y arrancando hoy. */
export function emptyDraft(today: string): PolicyDraft {
    return {
        name: '',
        description: '',
        suspend: false,
        levers: {},
        allowedSkus: [],
        targetLevels: [],
        storeIds: [],
        productSkus: [],
        segmentIds: [],
        validFrom: today,
        validTo: null,
    };
}

function sameLevers(left: PolicyLevers, right: PolicyLevers): boolean {
    return LEVER_KEYS.every((key) => {
        const a = left[key];
        const b = right[key];
        if (a === undefined || b === undefined) return a === b;
        return a.value === b.value && a.direction === b.direction;
    });
}

function sameList(left: readonly string[], right: readonly string[]): boolean {
    return left.length === right.length && left.every((value, i) => value === right[i]);
}

/** Cuántos campos cambió el operador. Cero = no hay nada que guardar. */
export function countDraftChanges(original: PolicyDraft, draft: PolicyDraft): number {
    let changes = 0;
    if (original.name !== draft.name) changes += 1;
    if (original.description !== draft.description) changes += 1;
    if (original.suspend !== draft.suspend) changes += 1;
    if (!sameLevers(original.levers, draft.levers)) changes += 1;
    if (!sameList(original.allowedSkus, draft.allowedSkus)) changes += 1;
    if (!sameList(original.targetLevels, draft.targetLevels)) changes += 1;
    if (!sameList(original.storeIds, draft.storeIds)) changes += 1;
    if (!sameList(original.productSkus, draft.productSkus)) changes += 1;
    if (!sameList(original.segmentIds, draft.segmentIds)) changes += 1;
    if (original.validFrom !== draft.validFrom) changes += 1;
    if (original.validTo !== draft.validTo) changes += 1;
    return changes;
}

/** Cotas de negocio de cada palanca, en las UNIDADES DE LA PANTALLA. Espejan las del backend. */
const LEVER_BOUNDS: Record<PolicyLeverKey, { min: number; max: number; unit: string }> = {
    sciMin: { min: 0, max: 100, unit: 'puntos' },
    downMinPct: { min: 0, max: 100, unit: '%' },
    tna: { min: 0, max: 500, unit: '%' },
    maxAmount: { min: 0, max: Number.MAX_SAFE_INTEGER, unit: 'pesos' },
};

/**
 * Lo que la pantalla puede rechazar antes de gastar un request. No duplica todas las reglas del
 * backend: solo las que el operador puede corregir sin adivinar.
 */
export function validateDraft(draft: PolicyDraft): string[] {
    const problems: string[] = [];
    if (draft.name.trim() === '') problems.push('Ponle un nombre a la política.');
    const levers = activeLevers(draft.levers);
    if (!draft.suspend && levers.length === 0 && draft.allowedSkus.length === 0) {
        problems.push(
            'La política no mueve nada: prende al menos una palanca, la suspensión o la lista de SKU permitidos.',
        );
    }
    for (const key of levers) {
        const lever = draft.levers[key];
        const bounds = LEVER_BOUNDS[key];
        if (lever === undefined) continue;
        if (!Number.isFinite(lever.value) || lever.value < bounds.min || lever.value > bounds.max) {
            problems.push(
                `${POLICY_LEVER_LABEL[key]} tiene que estar entre ${String(bounds.min)} y ${String(bounds.max)} ${bounds.unit}.`,
            );
        }
        if (lever.direction === 'ease' && !EASABLE_LEVERS.includes(key)) {
            problems.push(`${POLICY_LEVER_LABEL[key]} solo puede endurecer el plan.`);
        }
    }
    if (draft.validFrom !== null && draft.validTo !== null && draft.validTo < draft.validFrom) {
        problems.push('La fecha de fin es anterior a la de inicio.');
    }
    return problems;
}

// ---------------------------------------------------------------------------------------------
// Unidades: el contrato habla en decimales y cents; el editor, en puntos y pesos
// ---------------------------------------------------------------------------------------------

/** Puntos porcentuales tecleados → el decimal del contrato (20 → 0.2). */
export function pointsToDecimal(points: number): number {
    // Redondeo a 4 decimales: 20.5 puntos son 0.205 y no 0.20500000000000002.
    return Math.round(points * 100) / 10000;
}

/** El decimal del contrato → puntos porcentuales para el input (0.2 → 20). */
export function decimalToPoints(decimal: number): number {
    return Math.round(decimal * 10000) / 100;
}

/** Pesos tecleados → cents del contrato. Un monto de política nunca lleva fracción de centavo. */
export function pesosToCents(pesos: number): number {
    return Math.round(pesos * 100);
}

/** Cents del contrato → pesos para el input. */
export function centsToPesos(cents: number): number {
    return Math.round(cents) / 100;
}

// ---------------------------------------------------------------------------------------------
// Fallas de lectura: un 403 no es una caída
// ---------------------------------------------------------------------------------------------

export interface AuditFailureCopy {
    title: string;
    description: string;
    canRetry: boolean;
}

/**
 * Por qué no se pudo leer y si reintentar sirve. Un 403 se dice como falta de permiso y SIN botón:
 * insistir no lo cambia, y ofrecerlo manda al operador a golpear una puerta cerrada.
 */
export function readFailureCopy(
    error: unknown,
    subject: string,
    permission: string,
): AuditFailureCopy {
    const failure = classifyFailure(error) ?? 'unavailable';
    const described = describeFailure(failure, subject);
    if (failure === 'forbidden') {
        return {
            title: described.title,
            description: `Tu rol no incluye \`${permission}\`. Reintentar no lo cambia: contacta a quien administra los roles.`,
            canRetry: false,
        };
    }
    return {
        title: described.title,
        description: described.body,
        canRetry: isRetryableFailure(failure),
    };
}

/** El mismo aviso para la bitácora del tab Auditoría, que lee los planes. */
export function auditFailureCopy(error: unknown): AuditFailureCopy {
    return readFailureCopy(error, 'la bitácora', POLICIES_PERMISSIONS.read);
}

/**
 * Cada `code` de escritura del bloque con SU mensaje y con lo que el operador tiene que hacer.
 * Lo que no esté aquí cae a `userMessageOf`, nunca al `code` crudo ni a un `error.message` técnico.
 */
const MESSAGE_BY_STORE_POLICY_CODE: Record<StorePolicyErrorCode, string> = {
    STORE_POLICY_SEGMENT_UNKNOWN:
        'Un segmento del alcance ya no existe o está archivado. Refresca el selector de segmentos y vuelve a guardar.',
    STORE_POLICY_ARCHIVE_REASON_REQUIRED:
        'Para eliminar la política tienes que declarar el motivo. Escríbelo y vuelve a intentar.',
};

/** Si el `code` que llegó es uno del contrato, y no cualquier string del backend. */
function isStorePolicyErrorCode(code: string): code is StorePolicyErrorCode {
    return (STORE_POLICY_ERROR_CODES as readonly string[]).includes(code);
}

/** El mensaje que se le enseña al operador cuando falla un guardado de política especial. */
export function storePolicyErrorCopy(error: unknown): string {
    if (error instanceof ApiError && isStorePolicyErrorCode(error.code)) {
        return MESSAGE_BY_STORE_POLICY_CODE[error.code];
    }
    return userMessageOf(error);
}

/**
 * Corre un día `YYYY-MM-DD` en `delta` días. La bitácora particiona por día, así que navegar el
 * feed es moverse de partición, no filtrar.
 */
export function shiftDay(day: string, delta: number): string {
    const [year, month, date] = day.split('-').map(Number);
    const moved = new Date(Date.UTC(year, month - 1, date + delta));
    return moved.toISOString().slice(0, 10);
}
